import { useEffect } from "react";

/**
 * Injects a JSON-LD structured-data block into <head>.
 *
 * Phase 2: injected imperatively in the browser. Search engines that execute
 * JS (and the Phase 1 prerender/render layer) will pick it up. Each block is
 * keyed by `id` so multiple blocks can coexist on one page and are cleaned up
 * on unmount.
 *
 * Phase 1 (SSR / prerender) TODO: emit these blocks at render time so they are
 * present in the server HTML the crawler sees on the first pass.
 */

export interface JsonLdProps {
  /** Unique id per block on a page, e.g. "breadcrumb", "organization". */
  id: string;
  /** A schema.org object (will be JSON.stringify-ed). */
  data: Record<string, unknown>;
}

export function JsonLd({ id, data }: JsonLdProps) {
  useEffect(() => {
    const elementId = `jsonld-${id}`;
    let script = document.getElementById(elementId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = elementId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      document.getElementById(elementId)?.remove();
    };
  }, [id, data]);

  return null;
}

export default JsonLd;
